import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, Lock, Server, Key, FileCheck, Database, Eye, 
  UserCheck, RefreshCw, AlertTriangle, CheckCircle, 
  BadgeCheck, Globe, Cloud, Fingerprint, ShieldCheck,
  FileKey, Users, Activity, ClipboardCheck, PenTool, Hash
} from 'lucide-react';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';

// Animation wrapper
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Security Feature Card
const SecurityFeature = ({ icon: Icon, title, description, items, color = "indigo" }) => {
  const colorClasses = {
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-200",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    violet: "bg-violet-50 text-violet-600 border-violet-200",
    amber: "bg-amber-50 text-amber-600 border-amber-200",
    rose: "bg-rose-50 text-rose-600 border-rose-200"
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 hover:shadow-lg transition-all duration-300">
      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${colorClasses[color]}`}>
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-3">{title}</h3>
      <p className="text-slate-600 mb-4">{description}</p>
      {items && (
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Compliance Badge
const ComplianceBadge = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-slate-200">
    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
      <Icon className="w-6 h-6 text-slate-700" />
    </div>
    <div>
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="text-sm text-slate-500">{subtitle}</div>
    </div>
  </div>
);

export const SecurityPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-8 backdrop-blur-sm border border-white/20">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span className="text-white/90 text-sm font-medium">Enterprise-Grade Security</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Security & Compliance
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Your data security is our top priority. Formlio employs industry-leading security measures to protect every submission, ensuring compliance with global standards.
            </p>
          </AnimatedSection>

          {/* Trust Badges */}
          <AnimatedSection delay={0.2} className="mt-12 flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg backdrop-blur-sm">
              <BadgeCheck className="w-5 h-5 text-blue-400" />
              <span className="text-white text-sm">Google OAuth Verified</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg backdrop-blur-sm">
              <Lock className="w-5 h-5 text-emerald-400" />
              <span className="text-white text-sm">TLS Encrypted</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg backdrop-blur-sm">
              <ShieldCheck className="w-5 h-5 text-violet-400" />
              <span className="text-white text-sm">Secure Password Storage</span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Data Encryption Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full mb-6">
              Encryption
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Data Protection at Every Layer
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              All data is encrypted both in transit and at rest using industry-standard algorithms.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedSection delay={0.1}>
              <SecurityFeature
                icon={Lock}
                title="HTTPS/TLS Everywhere"
                description="All connections are secured with modern encryption."
                color="indigo"
                items={[
                  "TLS encryption on all endpoints",
                  "HSTS headers prevent downgrade attacks",
                  "Secure cloud infrastructure"
                ]}
              />
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <SecurityFeature
                icon={Database}
                title="Encryption at Rest"
                description="Your data is encrypted when stored in our databases."
                color="emerald"
                items={[
                  "Industry-standard encryption at rest",
                  "Encrypted database backups",
                  "Secure key management"
                ]}
              />
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <SecurityFeature
                icon={FileKey}
                title="Sensitive Data Handling"
                description="Extra protection for passwords and tokens."
                color="blue"
                items={[
                  "Secure password hashing (bcrypt)",
                  "Encrypted OAuth token storage",
                  "No plain-text sensitive data"
                ]}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Authentication Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-violet-100 text-violet-700 text-sm font-medium rounded-full mb-6">
              Authentication
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Secure Access Control
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Multi-layered authentication with industry best practices.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatedSection delay={0.1}>
              <SecurityFeature
                icon={Key}
                title="Token-Based Auth"
                description="Secure session management."
                color="violet"
                items={[
                  "Short-lived access tokens",
                  "Automatic session expiry",
                  "Secure token handling"
                ]}
              />
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <SecurityFeature
                icon={Fingerprint}
                title="Two-Factor Authentication"
                description="Additional security layer for accounts."
                color="rose"
                items={[
                  "Email-based verification codes",
                  "Login attempt notifications",
                  "Account recovery options"
                ]}
              />
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <SecurityFeature
                icon={Users}
                title="Role-Based Access"
                description="Granular permission control."
                color="amber"
                items={[
                  "Admin and Member roles",
                  "Team-level permissions",
                  "Form-specific access control"
                ]}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* File Upload & Database Security */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* File Upload Security */}
            <AnimatedSection>
              <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full mb-6">
                File Security
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-6">
                File Upload Protection
              </h2>
              <p className="text-slate-600 mb-8">
                Comprehensive validation and security measures for all file uploads.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: FileCheck, text: "File type and extension validation" },
                  { icon: AlertTriangle, text: "Executable file blocking (.exe, .bat, .sh, etc.)" },
                  { icon: Server, text: "File size limits (configurable per plan)" },
                  { icon: RefreshCw, text: "Automatic temp file cleanup" },
                  { icon: Lock, text: "Signed URL access for cloud storage" }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <item.icon className="w-5 h-5 text-emerald-600" />
                    <span className="text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Database Security */}
            <AnimatedSection delay={0.2}>
              <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-6">
                Database Protection
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-6">
                Database Security
              </h2>
              <p className="text-slate-600 mb-8">
                Multiple layers of protection for your stored data.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Database, text: "Parameterized queries prevent SQL injection" },
                  { icon: Shield, text: "Input validation and sanitization" },
                  { icon: Activity, text: "Rate limiting on all endpoints" },
                  { icon: Globe, text: "Multi-tenant data isolation" },
                  { icon: UserCheck, text: "Strict tenant-level filtering" }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <item.icon className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Monitoring & Audit Logs */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-medium rounded-full mb-6">
              Monitoring
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Logging & Monitoring
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive audit trails and real-time monitoring for security events.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedSection delay={0.1}>
              <SecurityFeature
                icon={Activity}
                title="Error Monitoring"
                description="Application health tracking."
                color="amber"
                items={[
                  "Error logging and alerts",
                  "Application monitoring",
                  "Performance tracking"
                ]}
              />
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <SecurityFeature
                icon={Eye}
                title="Access Monitoring"
                description="Track and review access patterns."
                color="rose"
                items={[
                  "Failed login attempt tracking",
                  "Rate limiting protection",
                  "Unusual activity alerts"
                ]}
              />
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <SecurityFeature
                icon={ClipboardCheck}
                title="Activity Logs"
                description="Record of important actions."
                color="indigo"
                items={[
                  "Admin action logging",
                  "User activity records",
                  "Data change history"
                ]}
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-slate-100 text-slate-700 text-sm font-medium rounded-full mb-6">
              Compliance
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Compliance & Certifications
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We maintain compliance with industry standards and regulations.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatedSection delay={0.1}>
              <ComplianceBadge
                icon={BadgeCheck}
                title="Google OAuth Verified"
                subtitle="Secure authentication"
              />
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <ComplianceBadge
                icon={Lock}
                title="TLS Encrypted"
                subtitle="All data in transit"
              />
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <ComplianceBadge
                icon={Shield}
                title="Security Best Practices"
                subtitle="Industry-standard controls"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Security Practices */}
      <section className="py-24 bg-gradient-to-br from-indigo-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Our Security Practices
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Security is built into every aspect of our development process.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Security Reviews", description: "Internal security assessments and code reviews" },
              { icon: RefreshCw, title: "Regular Updates", description: "Security patches and dependency updates" },
              { icon: Users, title: "Secure Development", description: "Security-focused development practices" },
              { icon: AlertTriangle, title: "Incident Response", description: "Monitoring with rapid incident response" }
            ].map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/10">
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-300">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Formlio Sign - Electronic Signature Security */}
      <section className="py-24 bg-gradient-to-b from-indigo-50 to-white" data-testid="formlio-sign-security">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <PenTool className="w-4 h-4" />
              Formlio Sign
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Electronic Signature Security
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Formlio Sign ensures your electronically signed documents are legally valid, tamper-proof, and verifiable.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <AnimatedSection delay={0.1}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm h-full">
                <Hash className="w-10 h-10 text-indigo-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Document Integrity (SHA-256)</h3>
                <p className="text-sm text-slate-600">
                  Every signed document is hashed with SHA-256. Any modification to the document content, signature, or metadata is immediately detectable via the verification endpoint.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm h-full">
                <ShieldCheck className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Public Verification</h3>
                <p className="text-sm text-slate-600">
                  Anyone can verify a signed document at <Link to="/verify" className="text-indigo-600 underline font-medium">formlio.app/verify</Link> using the unique document number. Verification shows status, form name, date, and integrity — never sensitive data.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm h-full">
                <FileCheck className="w-10 h-10 text-purple-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Legal Compliance</h3>
                <p className="text-sm text-slate-600">
                  Electronic signatures through Formlio Sign comply with the <strong>E-SIGN Act</strong>, <strong>UETA</strong>, and similar international regulations. See our{' '}
                  <Link to="/consumer-consent" className="text-indigo-600 underline font-medium">Consumer Consent Disclosure</Link>.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.4}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm h-full">
                <Lock className="w-10 h-10 text-slate-700 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">No Sensitive Data Exposure</h3>
                <p className="text-sm text-slate-600">
                  The verification endpoint only reveals document status, form name, date, and integrity hash. No form answers, email addresses, phone numbers, or uploaded files are ever exposed.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.5}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm h-full">
                <Key className="w-10 h-10 text-amber-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Unique Document Numbers</h3>
                <p className="text-sm text-slate-600">
                  Each signed document receives a sequential, unique identifier (FL-SIGN-YYYY-NNNNNN) that serves as a permanent reference for audit trails and verification.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.6}>
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm h-full">
                <PenTool className="w-10 h-10 text-indigo-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Consent & Disclosure</h3>
                <p className="text-sm text-slate-600">
                  Before signing, users are presented with a consent statement and a link to the full{' '}
                  <Link to="/consumer-consent" className="text-indigo-600 underline font-medium">Consumer Consent Disclosure</Link>, ensuring informed agreement.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
              Have Security Questions?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Our security team is here to help. Contact us for security assessments, compliance documentation, or any security-related inquiries.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                  Contact Security Team
                </Button>
              </Link>
              <Button variant="outline" size="lg">
                Download Security Whitepaper
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SecurityPage;
